import { useState } from 'react';
import { formatDuration, type ScoreType } from '@timecap/core';

import type { WorkoutSummary } from '../lib/api.js';

const TIPO: Record<ScoreType, string> = {
  time: 'Gana el menor tiempo',
  reps: 'Gana quien más repeticiones',
  load: 'Gana quien más kilos',
  distance: 'Gana quien más distancia',
};

/**
 * El WOD que se está juzgando.
 *
 * Faltaba, y era grave: el juez veía el nombre del WOD en un desplegable
 * y nada más. Ni qué hay que hacer, ni el time cap, ni si hay tiebreak.
 * Un juez que no sabe qué está juzgando no puede juzgar.
 *
 * Arranca plegado y se abre de un toque: durante el heat estorba, pero
 * entre heat y heat es lo primero que se consulta.
 */
export function WodCard({ workout }: { workout: WorkoutSummary }) {
  const [abierto, setAbierto] = useState(false);

  const cap = workout.time_cap_seconds ? formatDuration(workout.time_cap_seconds) : null;

  return (
    <section className={`wod ${abierto ? 'is-open' : ''}`}>
      <button className="wod-head" onClick={() => setAbierto((v) => !v)} aria-expanded={abierto}>
        <span className="wod-title">
          <strong>{workout.name}</strong>
          <small>
            {TIPO[workout.score_type]}
            {cap && <> · cap {cap}</>}
          </small>
        </span>
        <span className="wod-toggle" aria-hidden="true">
          {abierto ? '−' : '+'}
        </span>
      </button>

      {abierto && (
        <div className="wod-body">
          {workout.description ? (
            // El WOD tal cual lo escribió el organizador. Los saltos de
            // línea importan: un WOD es una lista, no un párrafo.
            <pre className="wod-desc">{workout.description}</pre>
          ) : (
            <p className="wod-desc muted">
              El organizador no escribió el WOD. Pídeselo antes de empezar.
            </p>
          )}

          <ul className="wod-meta">
            {cap && (
              <li>
                <span>Time cap</span>
                <strong>{cap}</strong>
              </li>
            )}
            {workout.rep_target != null && (
              <li>
                <span>Reps totales</span>
                <strong>{workout.rep_target}</strong>
              </li>
            )}
            {workout.tiebreak_enabled && (
              <li>
                <span>Tiebreak</span>
                <strong>{workout.tiebreak_label || 'sí'}</strong>
              </li>
            )}
            {workout.weight !== 1 && (
              <li>
                <span>Puntos</span>
                <strong>×{workout.weight}</strong>
              </li>
            )}
          </ul>
        </div>
      )}
    </section>
  );
}
